import React, { memo, useState, useCallback, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import withStyles from '@material-ui/core/styles/withStyles';
import List from '@material-ui/core/List';
import { injectIntl } from 'react-intl';
import { Checkbox, FormControlLabel, Button, Typography } from '@material-ui/core';
import { getPropertType, keyEmptyCheck } from 'aurora-utils/utils';
import messages from 'aurora-translations';
import isArray from 'lodash/isArray';
import isEmpty from 'lodash/isEmpty';
import ceil from 'lodash/ceil';
import { CleverTapScreenPropertiesOwnership, CleverTapScreenPropertiesLease } from 'aurora-utils/cleverTap.eventhelper';
import { ADGMModal, ADGMImage, ADGMPropertyCard, ADGMPagination } from 'aurora-components';
import PropertySummary from './Summary';
import styles from './styles';

const PAGE_SIZE = 5;
const DEFAULT_PAGE = 1;

function PropertyList(props) {
  const {
    classes,
    data,
    locale,
    isLease,
    isMultiLease,
    isOwnership,
    isMortgage,
    isPMA,
    isPermit,
    history,
    intl,
    selected,
    setSelected,
    selectable,
    multi,
    isPMABank,
    isPmc,
    ...otherProps
  } = props;
  const [expandedId, setExpandedId] = useState(null);
  const [selectAll, setSelectAll] = useState(null);
  const [openSelectedModal, setOpenSelectedModal] = useState(false);
  const [page, setPage] = useState(DEFAULT_PAGE);

  const endIndex = useMemo(() => parseInt(page, 10) * PAGE_SIZE, [page]);
  const startIndex = useMemo(() => endIndex - PAGE_SIZE, [endIndex]);

  const isArabic = useMemo(() => locale === 'ar', [locale]);

  const properties = useMemo(() => {
    if (isLease) {
      return data?.properties?.map((prop, index) => ({
        id: index + 1,
        ...prop,
        buildingName: isArabic
          ? prop?.buildingNameAr || prop?.buildingNameEn
          : prop?.buildingNameEn || prop?.buildingNameAr,
        communityName: isArabic ? prop?.communityAr || prop?.communityEn : prop?.communityEn || prop?.communityAr,
        constructedStatusName: isArabic
          ? prop?.constructedStatusNameAr || prop?.constructedStatusNameEn
          : prop?.constructedStatusNameEn || prop?.constructedStatusNameAr,
        districtName: isArabic ? prop?.districtAr || prop?.districtEn : prop?.districtEn || prop?.districtAr,
        landUseName: isArabic ? prop?.landUseNameAr || prop?.landUseNameEn : prop?.landUseNameEn || prop?.landUseNameAr,
        lastTransactionType: isArabic
          ? prop?.lastTransactionTypeAr || prop?.lastTransactionTypeEn
          : prop?.lastTransactionTypeEn || prop?.lastTransactionTypeAr,
        ownerShipTypeName: isArabic
          ? prop?.ownerShipTypeNameAr || prop?.ownerShipTypeNameEn
          : prop?.ownerShipTypeNameEn || prop?.ownerShipTypeNameAr,
        parentLandUseName: isArabic
          ? prop?.parentLandUseNameAr || prop?.parentLandUseNameEn
          : prop?.parentLandUseNameEn || prop?.parentLandUseNameAr,
        plotAllocationName: isArabic
          ? prop?.plotAllocationNameAr || prop?.plotAllocationNameEn
          : prop?.plotAllocationNameEn || prop?.plotAllocationNameAr,
        roadName: isArabic ? prop?.roadAr || prop?.roadEn : prop?.roadEn || prop?.roadAr,
        serviceStatusName: isArabic
          ? prop?.serviceStatusNameAr || prop?.serviceStatusNameEn
          : prop?.serviceStatusNameEn || prop?.serviceStatusNameAr,
        unitClassification: isArabic
          ? prop?.unitClassificationAr || prop?.unitClassificationEn
          : prop?.unitClassificationEn || prop?.unitClassificationAr,
        unitType: isArabic ? prop?.unitTypeAr || prop?.unitTypeEn : prop?.unitTypeEn || prop?.unitTypeAr,
        unitUsageName: isArabic
          ? prop?.unitUsageNameAr || prop?.unitUsageNameEn
          : prop?.unitUsageNameEn || prop?.unitUsageNameAr,
        buildingUsageName: isArabic
          ? prop?.buildingUsageNameAr || prop?.buildingUsageNameEn
          : prop?.buildingUsageNameEn || prop?.buildingUsageNameAr,
      }));
    }
    if (isOwnership) {
      return data?.properties?.map((prop, index) => ({
        id: index + 1,
        ...prop,
        buildingName: isArabic
          ? prop?.buildingNameAr || prop?.buildingNameEn
          : prop?.buildingNameEn || prop?.buildingNameAr,
        communityName: isArabic ? prop?.communityAr || prop?.communityEn : prop?.communityEn || prop?.communityAr,
        districtName: isArabic ? prop?.districtAr || prop?.districtEn : prop?.districtEn || prop?.districtAr,
        landUseName: isArabic ? prop?.landUseNameAr || prop?.landUseNameEn : prop?.landUseNameEn || prop?.landUseNameAr,
        ownerShipTypeName: isArabic
          ? prop?.ownerShipTypeNameAr || prop?.ownerShipTypeNameEn
          : prop?.ownerShipTypeNameEn || prop?.ownerShipTypeNameAr,
        parentLandUseName: isArabic
          ? prop?.parentLandUseNameAr || prop?.parentLandUseNameEn
          : prop?.parentLandUseNameEn || prop?.parentLandUseNameAr,
        plotAllocationName: isArabic
          ? prop?.plotAllocationNameAr || prop?.plotAllocationNameEn
          : prop?.plotAllocationNameEn || prop?.plotAllocationNameAr,
        lastTransactionType: isArabic
          ? prop?.lastTransactionTypeAr || prop?.lastTransactionTypeEn
          : prop?.lastTransactionTypeEn || prop?.lastTransactionTypeAr,
        serviceStatusName: isArabic
          ? prop?.serviceStatusNameAr || prop?.serviceStatusNameEn
          : prop?.serviceStatusNameEn || prop?.serviceStatusNameAr,
        unitClassification: isArabic
          ? prop?.unitClassificationAr || prop?.unitClassificationEn
          : prop?.unitClassificationEn || prop?.unitClassificationAr,
        unitUsageName: isArabic
          ? prop?.unitUsageNameAr || prop?.unitUsageNameEn
          : prop?.unitUsageNameEn || prop?.unitUsageNameAr,
        roadName: isArabic ? prop?.roadAr || prop?.roadEn : prop?.roadEn || prop?.roadAr,
        buildingUsageName: isArabic
          ? prop?.buildingUsageNameAr || prop?.buildingUsageNameEn
          : prop?.buildingUsageNameEn || prop?.buildingUsageNameAr,
      }));
    }
    if (isMortgage) {
      return data?.properties?.map((prop, index) => ({
        id: index + 1,
        ...prop,
        buildingName: isArabic
          ? prop?.buildingNameAr || prop?.buildingNameEn
          : prop?.buildingNameEn || prop?.buildingNameAr,
        districtName: isArabic ? prop?.districtAr || prop?.districtEn : prop?.districtEn || prop?.districtAr,
        communityName: isArabic ? prop?.communityAr : prop?.communityEn,
        landUseName: isArabic ? prop?.landUseNameAr || prop?.landUseNameEn : prop?.landUseNameEn || prop?.landUseNameAr,
        unitUsageName: isArabic
          ? prop?.unitUsageNameAr || prop?.unitUsageNameEn
          : prop?.unitUsageNameEn || prop?.unitUsageNameAr,
        mortgageType: isArabic ? prop?.mortgageTypeAr : prop?.mortgageTypeEn,
        roadName: isArabic ? prop?.roadAr || prop?.roadEn : prop?.roadEn || prop?.roadAr,
        ownerShipTypeName: isArabic
          ? prop?.ownerShipTypeNameAr || prop?.ownerShipTypeNameEn
          : prop?.ownerShipTypeNameEn || prop?.ownerShipTypeNameAr,
        buildingUsageName: isArabic
          ? prop?.buildingUsageNameAr || prop?.buildingUsageNameEn
          : prop?.buildingUsageNameEn || prop?.buildingUsageNameAr,
        bankName: isArabic ? prop?.bankNameAr : prop?.bankNameEn,
      }));
    }
    return [];
  }, [data, isLease, isOwnership, isMortgage]);

  const getPropertyTitle = useCallback(
    prop => {
      const type = getPropertType(prop?.propertyType);
      let title = null;
      let subTitle = null;
      if (type === 'Land') {
        title = `${intl.formatMessage({ ...messages.plot_number })} ${prop?.plotNumber}`;

        const subTitleArr = [];
        prop?.roadNumber && subTitleArr.push(`${intl.formatMessage({ ...messages.Road })} ${prop?.roadNumber}`);
        // prop?.communityName && subTitleArr.push(prop.communityName);
        // prop?.districtName && subTitleArr.push(prop.districtName);
        subTitle = subTitleArr.join(', ');
      }
      if (type === 'Unit') {
        title = isLease
          ? ` ${prop?.unitRegNum}`
          : `${intl.formatMessage({ ...messages.unit_number })}: ${prop?.unitNumber}`;
        const subTitleArr = [];
        isLease &&
          prop?.unitNumber &&
          subTitleArr.push(`${intl.formatMessage({ ...messages.unit_number })} ${prop?.unitNumber}`);
        prop?.buildingNumber &&
          subTitleArr.push(`${intl.formatMessage({ ...messages.building_name })} ${prop?.buildingNumber}`);
        // !isLease &&
        //   prop?.unitNumber &&
        //   subTitleArr.push(`${intl.formatMessage({ ...messages.plot_number })}: ${prop?.plotNumber}`);
        subTitle = subTitleArr.join('\r\n');
      }
      if (['Building', 'Villa'].includes(type)) {
        title = prop?.buildingRegNum;
        const subTitleArr = [];
        prop?.buildingNumber &&
          subTitleArr.push(
            `${intl.formatMessage({ ...messages.building_name })} ${
              isArabic ? prop?.buildingNameAr : prop?.buildingNameEn
            }`,
          );
        subTitle = subTitleArr.join('\r\n');
      }
      return { title, subTitle };
    },
    [isLease],
  );

  const viewPMAUnits = building => {
    history.push({
      pathname: `/app/services/pma-selected-building-units/`,
      search:
        window.location.search +
        '&buildingRegNumber=' +
        building?.buildingRegNumber +
        '&isSelectable=true' +
        `&isPMABank=${isPMABank}`,
      state: { building },
    });
  };

  const viewPermitUnits = building => {
    history.push({
      pathname: `/app/services/request-permit-selected-building-units/`,
      search: window.location.search + '&buildingRegNumber=' + building?.buildingRegNumber + '&isSelectable=true',
      state: { building },
    });
  };

  useEffect(() => {
    isOwnership && CleverTapScreenPropertiesOwnership();
    isLease && CleverTapScreenPropertiesLease();
  }, [isLease, isOwnership]);

  const closeModalHandler = () => {
    setOpenSelectedModal(false);
  };
  // const showSelectedPropertiesModalHandler = () => {
  //   if (isArray(selected) && selected?.length > 0) {
  //     setOpenSelectedModal(true);
  //   }
  // };

  const selectAllHandler = e => {
    if (e.target.checked) {
      setSelectAll(true);
      // const newProperties = properties?.filter(
      //   property =>
      //     selected?.find(selectedProperty => selectedProperty?.propertyID === property?.propertyID) === undefined,
      // );
      // setSelected([...selected, ...newProperties]);

      setSelected([...properties]);
    } else {
      setSelectAll(false);
      setSelected(
        selected?.filter(
          selectedProperty =>
            properties?.find(property => property?.propertyID === selectedProperty?.propertyID) === undefined,
        ),
      );
    }
  };

  const handlePageChange = num => {
    setPage(num);
  };

  return (
    <>
      {!isEmpty(properties) && !isPMA && selectable && multi && (
        <div className={classes.selectAllContainer}>
          <FormControlLabel
            className={classes.inputCheckbox}
            onChange={selectAllHandler}
            label={keyEmptyCheck('select_all_on_page')}
            control={
              <Checkbox
                edge='start'
                checked={selectAll}
                indeterminate={isArray(selected) && selected?.length > 1}
                classes={{
                  root: classes.itemCheckbox,
                  checked: classes.itemCheckboxChecked,
                  indeterminate: classes.itemCheckboxIndeterminate,
                }}
              />
            }
          />
          {/* <Button
            className={classes.viewSelectedPlotsBtn}
            onClick={showSelectedPropertiesModalHandler}
            disabled={!isArray(selected) || isEmpty(selected)}
          >
            {keyEmptyCheck('view_selected_plots', { s: isArray(selected) ? selected?.length : 0 })}
          </Button> */}
        </div>
      )}

      <List className={classes.propertiesList}>
        {properties?.map(item => (
          <PropertySummary
            key={item.id}
            locale={locale}
            property={item}
            isLease={isLease}
            isMultiLease={isMultiLease}
            isOwnership={isOwnership}
            isMortgage={isMortgage}
            expandedId={expandedId}
            setExpandedId={setExpandedId}
            getPropertyTitle={getPropertyTitle}
            viewPMAUnits={viewPMAUnits}
            viewPermitUnits={viewPermitUnits}
            isPMA={isPMA}
            isPermit={isPermit}
            selected={selected}
            setSelected={setSelected}
            selectable={selectable}
            multi={multi}
            isPMABank={isPMABank}
            isPmc={isPmc}
            {...otherProps}
          />
        ))}
      </List>

      {openSelectedModal && (
        <ADGMModal
          className={classes.selectedPlotsModal}
          open={openSelectedModal}
          onClose={closeModalHandler}
          disableScrollLock={false}
        >
          <div className={classes.selectedPlotsTitleContainer}>
            <Typography variant='h5' className={classes.selectedPlotsTitle}>
              {keyEmptyCheck('selected_plots', { s: isArray(selected) ? selected?.length : 0 })}
            </Typography>
            <Button variant='contained' className={classes.closeBtn} onClick={closeModalHandler}>
              <ADGMImage name='close.svg' alt='close' />
            </Button>
          </div>
          <div className={classes.selectedPlotsModalContent}>
            <div className={classes.propertyCardsList}>
              {isArray(selected) &&
                selected
                  ?.slice(startIndex, endIndex)
                  ?.map(property => (
                    <ADGMPropertyCard
                      property={property}
                      isLease={isLease}
                      isOwnership={isOwnership}
                      isMortgage={isMortgage}
                    />
                  ))}
            </div>
            <ADGMPagination
              totalPages={ceil(selected?.length / PAGE_SIZE)}
              currentPage={Number(page)}
              onPageChange={handlePageChange}
              customClasses={{ container: classes.selectedPlotsModalPagination }}
            />
          </div>
          <div className={classes.selectedPlotsModalFooter}>
            <Button
              variant='contained'
              className={classes.resetBtn}
              onClick={() => {
                setSelected([]);
                setPage(DEFAULT_PAGE);
                setSelectAll(false);
                setOpenSelectedModal(false);
              }}
            >
              {keyEmptyCheck('clear_all_selections')}
            </Button>
            <Button variant='contained' className={classes.closeModalBtn} onClick={closeModalHandler}>
              {keyEmptyCheck('close')}
            </Button>
          </div>
        </ADGMModal>
      )}
    </>
  );
}

PropertyList.propTypes = {
  classes: PropTypes.object.isRequired,
  locale: PropTypes.string.isRequired,
  isLease: PropTypes.bool.isRequired,
  isMultiLease: PropTypes.bool.isRequired,
  isOwnership: PropTypes.bool.isRequired,
  isMortgage: PropTypes.bool.isRequired,
  data: PropTypes.object.isRequired,
  isPMA: PropTypes.object,
  history: PropTypes.object,
  intl: PropTypes.object.isRequired,
  selected: PropTypes.array.isRequired,
  setSelected: PropTypes.func.isRequired,
  selectable: PropTypes.bool.isRequired,
  multi: PropTypes.bool.isRequired,
  isPMABank: PropTypes.bool.isRequired,
  isPmc: PropTypes.bool,
  isPermit: PropTypes.bool,
};
PropertyList.defaultProps = {
  isPMA: false,
  isPmc: false,
  history: {},
  isPermit: false,
};

export default withStyles(styles)(injectIntl(memo(PropertyList)));
